/**
 * Default-scope preference for new sessions, persisted to localStorage.
 * Used by ChatPage's boot() before POSTing /api/sessions, and by
 * SettingsPage to render and update the user-facing toggle.
 *
 * Scope is immutable per session (enforced server-side by sessions.ts PATCH
 * destructuring only title/status); this preference governs ONLY the scope
 * a newly-created session is born with.
 */

const STORAGE_KEY = 'fen.scope.default';

// Local literal union — client bundle cannot import the runtime pgEnum from
// shared/schema.ts without pulling Drizzle in. Mirrors the server's local
// declaration in server/routes/sessions.ts. UF-U6 (task #775): 'builds' joins
// the set — the three lists (here, sessions.ts, shared enum) move together.
export type SessionScope = 'personal_life' | 'everfen_business' | 'builds';

const VALID_SCOPES: readonly SessionScope[] = ['personal_life', 'everfen_business', 'builds'];

/** Default-default when no preference is set (first-visit). */
const DEFAULT_SCOPE: SessionScope = 'everfen_business';

export function isSessionScope(value: unknown): value is SessionScope {
  return typeof value === 'string' && (VALID_SCOPES as readonly string[]).includes(value);
}

export function getScopePreference(): SessionScope {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isSessionScope(stored) ? stored : DEFAULT_SCOPE;
  } catch {
    // localStorage access can throw in certain privacy modes; fall back.
    return DEFAULT_SCOPE;
  }
}

export function setScopePreference(scope: SessionScope): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, scope);
  } catch {
    // Safari private mode throws on write; degrade silently — the in-memory
    // state in SettingsPage still reflects the user's choice for this tab.
    console.warn('[scopePreference] localStorage write failed; preference not persisted');
  }
}
